import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import ArticleOutlined from '@mui/icons-material/ArticleOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import PortalLayout from '../../components/layouts/PortalLayout';
import { theme } from '@omnigenbiodata/ui';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { TRANSLATION_VARS } from '../../core/constants/features.constants';

const primaryTint = theme.palette.grey[100];
const primaryTintStrong = theme.palette.info.lighter;

interface InformationSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

function InformationSection({ title, icon, children }: InformationSectionProps) {
  return (
    <Paper
      elevation={2}
      sx={{
        border: 1,
        borderColor: 'rgba(16, 53, 68, 0.16)',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Box p={{ xs: 3, md: 4 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: primaryTint,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>

          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              lineHeight: 1.15,
            }}
          >
            {title}
          </Typography>
        </Box>

        <Typography
          variant="h6"
          component="div"
          color="text.secondary"
          sx={{
            lineHeight: 1.45,
          }}
        >
          {children}
        </Typography>
      </Box>
    </Paper>
  );
}

function ParticipantInformationScene() {
  const { t } = useTranslation('pis');
  return (
    <PortalLayout>
      <Box pb={3}>
        <Paper
          elevation={4}
          sx={{
            mb: 4,
            border: 1,
            borderColor: 'rgba(16, 53, 68, 0.18)',
            bgcolor: primaryTint,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: 12,
              bgcolor: 'primary.main',
            }}
          />

          <Box p={{ xs: 3, md: 5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 2,
              }}
            >
              <Box
                sx={{
                  width: 84,
                  height: 84,
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'background.paper',
                  flexShrink: 0,
                }}
              >
                <ArticleOutlined color="primary" sx={{ fontSize: 52 }} />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Chip
                  label="Participant Information Sheet"
                  color="primary"
                  variant="outlined"
                  sx={{
                    mb: 1,
                    fontWeight: 700,
                  }}
                />

                <Typography
                  variant="h3"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                    lineHeight: 1.1,
                  }}
                >
                  {t('title')}
                </Typography>
              </Box>
            </Box>

            <Typography
              variant="h6"
              component="p"
              color="text.secondary"
              sx={{
                maxWidth: 920,
                lineHeight: 1.35,
              }}
            >
              Please read the participant information below. This page will contain the study information shown to
              participants before they decide whether to continue.
            </Typography>
          </Box>
        </Paper>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Grid container spacing={3}>
              {Object.values(
                t('sections', {
                  returnObjects: true,
                  ...TRANSLATION_VARS,
                }),
              ).map((s: any, sIndex: number) => (
                <Grid item xs={12} key={sIndex}>
                  <InformationSection title={s.title} icon={<InfoOutlined color="primary" sx={{ fontSize: 34 }} />}>
                    <ReactMarkdown linkTarget="_blank">{s.text}</ReactMarkdown>
                  </InformationSection>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={4}
              sx={{
                position: { md: 'sticky' },
                top: { md: 24 },
                border: 1,
                borderColor: 'rgba(16, 53, 68, 0.18)',
                bgcolor: primaryTintStrong,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: 8,
                  bgcolor: 'primary.main',
                }}
              />

              <Box p={3}>
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                    mb: 1,
                  }}
                >
                  Information Summary
                </Typography>

                <Typography variant="body1" component="p" color="text.secondary" sx={{ lineHeight: 1.45, mb: 3 }}>
                  This page contains the Participant Information Sheet. The real study text can be added here later.
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                  <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                    Can I take part in Discover Me?
                  </Typography>
                  <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                    What will happen if I decide to take part?
                  </Typography>
                  <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                    What happens during enrolment?
                  </Typography>
                  <Typography component="li" variant="body1" color="text.secondary">
                    What happens next?
                  </Typography>
                  <Typography component="li" variant="body1" color="text.secondary">
                    What happens to my sample?
                  </Typography>
                  <Typography component="li" variant="body1" color="text.secondary">
                    What information is collected about me?
                  </Typography>
                  <Typography component="li" variant="body1" color="text.secondary">
                    What happens to my information and how will it be used for this study?
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </PortalLayout>
  );
}

export default ParticipantInformationScene;
